import { useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Papa from "papaparse";
import { ArrowLeft, Upload, ArrowRight, CheckCircle2, AlertTriangle, ShieldCheck, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { downloadCsvTemplate, CSV_TEMPLATE_HEADERS } from "../lib/csvTemplate";

// The Status column tells the importer whether a row lands on the Leads
// table or the Clients table. Listed FIRST so the auto-guess picks it
// over the "stage" field when a CSV column is named "status" — the
// generic label most exports use for lead-vs-client. Values are matched
// loosely ("Client", "closed", "converted" → client; "lead", "new",
// "warm" → lead) on the server.
const TARGET_FIELDS = [
  { key: "status", label: "Status (Lead / Client)" },
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "full_name", label: "Full name (fallback)" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Address" },
  { key: "source", label: "Source" },
  { key: "type", label: "Type (buyer/seller/both)" },
  { key: "property_interest", label: "Property interest" },
  { key: "budget_min", label: "Price min" },
  { key: "budget_max", label: "Price max" },
  { key: "stage", label: "Lead pipeline stage" },
  { key: "target_areas", label: "Target areas (leads only)" },
  { key: "pre_approved", label: "Pre-approved" },
  { key: "lender_name", label: "Lender name" },
  { key: "sms_consent", label: "SMS consent" },
  { key: "email_consent", label: "Email consent" },
  { key: "consent_source", label: "Consent source" },
  { key: "notes", label: "Notes" },
];

// Heuristic guesses so agents don't map every column by hand. ORDER
// MATTERS: `guessMapping` iterates this object and returns the first
// alias hit — `status` sits at the top on purpose so a column headed
// "Status" resolves to the Lead/Client selector, not the pipeline stage.
const HEADER_ALIASES = {
  status: ["status", "record_type", "record_status", "contact_type", "customer_type"],
  first_name: ["first_name", "firstname", "first", "given_name", "given"],
  last_name: ["last_name", "lastname", "last", "surname", "family_name", "family"],
  full_name: ["full_name", "name", "full name", "contact", "contact name", "lead", "lead name", "client name"],
  email: ["email", "email_address", "e-mail", "mail"],
  phone: ["phone", "phone_number", "mobile", "cell", "telephone", "number"],
  address: ["address", "street_address", "mailing_address", "home_address", "location_address"],
  source: ["source", "lead_source", "referred_by", "origin"],
  // Note: "status" is deliberately absent here — it's owned by the
  // Lead/Client resolver above. Only pipeline-specific aliases remain.
  stage: ["stage", "pipeline", "pipeline_stage", "lead_stage"],
  // Unified type column — the parser routes to lead_type or client_type
  // based on the resolved record type.
  type: ["type", "lead_type", "client_type", "buyer_or_seller", "role"],
  property_interest: ["property_interest", "interest", "property", "wants", "looking_for"],
  budget_min: ["budget_min", "min_budget", "min_price", "price_min", "price_from"],
  budget_max: ["budget_max", "max_budget", "max_price", "price_max", "price_ceiling", "price_to"],
  target_areas: ["target_areas", "areas", "neighborhoods", "city", "cities"],
  pre_approved: ["pre_approved", "preapproved", "financing"],
  lender_name: ["lender", "lender_name", "mortgage"],
  sms_consent: ["sms_consent", "text_consent", "sms_ok", "text_ok"],
  email_consent: ["email_consent", "email_opt_in", "marketing_consent"],
  consent_source: ["consent_source", "opt_in_source"],
  notes: ["notes", "note", "comments", "comment", "description"],
};

function guessMapping(headers) {
  const guess = {};
  headers.forEach(h => {
    const norm = h.trim().toLowerCase().replace(/\s+/g, "_");
    for (const [target, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.includes(norm)) { guess[h] = target; return; }
    }
    // partial match
    for (const [target, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.some(a => norm.includes(a))) { guess[h] = target; return; }
    }
  });
  return guess;
}

export default function ImportLeads() {
  const nav = useNavigate();
  // Route context: `/leads/import` imports leads by default, while
  // `/clients/import` imports clients by default. The Status column
  // still overrides on a per-row basis in both.
  const { pathname } = useLocation();
  const mode = pathname.startsWith("/clients") ? "clients" : "leads";
  const defaultStatus = mode === "clients" ? "client" : "lead";
  const backHref = mode === "clients" ? "/clients" : "/leads";
  const backLabel = mode === "clients" ? "Back to clients" : "Back to leads";
  const headline = mode === "clients" ? "Import clients from CSV" : "Import leads from CSV";
  const helper = mode === "clients"
    ? "Load your existing client roster (or a mixed CSV) — rows without a Status column land as Clients by default, and their portal invites are ready to send once your Brokerage & licensing profile is complete."
    : "Zillow export, MLS drop, or anything with headers. We'll match your columns, catch duplicates, and respect your consent flags.";

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]); // parsed rows
  const [mapping, setMapping] = useState({});
  const [onDuplicate, setOnDuplicate] = useState("skip");
  const [consentAcknowledge, setConsentAcknowledge] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  // Bulk-invite state — populated on the result screen when the import
  // created one or more new Client rows. `bulkInviteResult` gates the
  // button back to a disabled "Invites sent" once fired so we can't
  // accidentally send twice.
  const [bulkInviting, setBulkInviting] = useState(false);
  const [bulkInviteResult, setBulkInviteResult] = useState(null);

  // The preview table renders one row per CSV entry, and each row iterates
  // `TARGET_FIELDS.filter(...)` twice — memoise once against `mapping` so a
  // 500-row preview doesn't do 1000 filter+values-lookups on every keystroke.
  const activeFields = useMemo(
    () => TARGET_FIELDS.filter(t => Object.values(mapping).includes(t.key)),
    [mapping],
  );

  const parseFile = (f) => {
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const hs = res.meta.fields || [];
        setFile(f);
        setHeaders(hs);
        setRows(res.data);
        setMapping(guessMapping(hs));
        setStep(2);
      },
      error: (err) => toast.error("CSV parse failed: " + err.message),
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) parseFile(f);
  };

  const mappedRequiredMissing = useMemo(() => {
    const mapped = new Set(Object.values(mapping));
    return TARGET_FIELDS.filter(t => t.required && !mapped.has(t.key)).map(t => t.label);
  }, [mapping]);

  const preview = useMemo(() => {
    return rows.slice(0, 5).map(r => {
      const mapped = {};
      Object.entries(mapping).forEach(([h, target]) => {
        if (target) mapped[target] = r[h];
      });
      return mapped;
    });
  }, [rows, mapping]);

  // Only recompute the "rows that couldn't import" list when the result
  // payload changes — avoids re-filtering on every render (result payloads
  // for big CSVs can have thousands of rows).
  const erroredRows = useMemo(() => {
    if (!result?.results) return [];
    return result.results.filter(r => r.status === "error").slice(0, 20);
  }, [result]);

  // Client IDs that were freshly created in THIS import run — feeds the
  // "Send portal invites" bulk action. Deliberately exclude
  // "updated" / "skipped" rows: those clients almost certainly already
  // got their invite the first time they were added.
  const newlyCreatedClientIds = useMemo(() => {
    if (!result?.results) return [];
    return result.results
      .filter(r => r.status === "created" && r.record_type === "client" && r.client_id)
      .map(r => r.client_id);
  }, [result]);

  const bulkInvite = async () => {
    if (!newlyCreatedClientIds.length) return;
    setBulkInviting(true);
    try {
      const { data } = await api.post("/clients/bulk-invite", {
        client_ids: newlyCreatedClientIds,
        origin_url: window.location.origin,
      });
      setBulkInviteResult(data);
      toast.success(
        `${data.sent} invite${data.sent === 1 ? "" : "s"} queued` +
          (data.skipped ? ` · ${data.skipped} skipped` : "") +
          (data.errored ? ` · ${data.errored} failed` : "")
      );
    } catch (err) {
      // Broker-disclosure guard 400 — same shape as the single-client
      // endpoint, same toast + deep-link to Settings.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "disclosure_incomplete") {
        toast.error(detail.message, {
          duration: 8000,
          action: {
            label: "Open Settings",
            onClick: () => window.location.assign("/settings#brokerage-licensing"),
          },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Bulk invite failed");
      }
    } finally { setBulkInviting(false); }
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const records = rows.map((r, i) => {
        const data = {};
        Object.entries(mapping).forEach(([h, target]) => {
          if (target) data[target] = r[h];
        });
        return { row_index: i, data };
      });
      const { data } = await api.post("/leads/import", {
        records,
        on_duplicate: onDuplicate,
        default_status: defaultStatus,
      });
      setResult(data);
      setStep(4);
      const createdBits = [];
      if (data.created_leads) createdBits.push(`${data.created_leads} lead${data.created_leads === 1 ? "" : "s"}`);
      if (data.created_clients) createdBits.push(`${data.created_clients} client${data.created_clients === 1 ? "" : "s"}`);
      const createdSummary = createdBits.length ? `Created ${createdBits.join(" + ")}` : "Nothing new created";
      toast.success(`${createdSummary} · Updated ${data.updated} · Skipped ${data.skipped}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const Stepper = () => (
    <ol className="flex items-center gap-2 mb-8">
      {["Upload", "Map", "Confirm", "Result"].map((label, i) => {
        const n = i + 1;
        return (
          <li key={label} className="flex items-center gap-2">
            <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${step === n ? "text-white" : step > n ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`} style={step === n ? { background: "var(--primary-hex)" } : {}}>
              {n}
            </span>
            <span className={`text-sm font-medium ${step >= n ? "text-slate-900" : "text-slate-500"}`}>{label}</span>
            {n < 4 && <span className="w-8 h-px bg-slate-300 mx-1" />}
          </li>
        );
      })}
    </ol>
  );

  return (
    <div className="max-w-4xl">
      <Link to={backHref} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid={`back-to-${mode}-import`}>
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </Link>
      <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">{headline}</h1>
      <p className="mt-1 text-sm text-slate-500">{helper}</p>
      {mode === "clients" && (
        <p className="mt-2 text-xs text-slate-500">
          <span className="font-semibold text-slate-700">Tip:</span> a <span className="font-mono">Status</span> column lets you mix Leads and Clients in one file — rows marked <span className="font-mono">Lead</span> land on the Leads tab, everything else becomes a Client.
        </p>
      )}

      {/* Download template — same headers on both tabs so agents can
          reuse one file. Rendered as a real <button> (not a Link) so
          browsers treat it like an in-app action, not a nav event. */}
      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
        <Download className="h-4 w-4 text-slate-500 shrink-0" />
        <div className="text-xs text-slate-600 min-w-0 flex-1">
          Not sure what to put in the file? Grab our template — <span className="font-semibold text-slate-900">{CSV_TEMPLATE_HEADERS.length} columns</span> covering every Lead + Client field, plus one sample row to copy from.
        </div>
        <button
          type="button"
          onClick={() => downloadCsvTemplate(mode === "clients" ? "ikey-clients-import-template.csv" : "ikey-leads-import-template.csv")}
          className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold text-white shrink-0"
          style={{ background: "var(--primary-hex)" }}
          data-testid="import-download-template"
        >
          <Download className="h-3.5 w-3.5" /> Download CSV template
        </button>
      </div>

      <div className="mt-8"><Stepper /></div>

      {step === 1 && (
        <label onDrop={onDrop} onDragOver={e => e.preventDefault()} className="block rounded-lg border-2 border-dashed border-slate-300 bg-white p-16 text-center cursor-pointer hover:border-slate-400" data-testid="import-dropzone">
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={e => e.target.files?.[0] && parseFile(e.target.files[0])} data-testid="import-file-input" />
          <Upload className="h-10 w-10 mx-auto text-slate-400" />
          <p className="mt-4 font-heading text-lg font-semibold text-slate-900">Drop a CSV file here</p>
          <p className="mt-1 text-sm text-slate-500">or click to browse. The first row should be column headers.</p>
        </label>
      )}

      {step === 2 && (
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-baseline justify-between gap-4 mb-4">
            <div>
              <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Map your columns</h2>
              <p className="text-xs text-slate-500 mt-0.5">{file?.name} · {rows.length} rows · {headers.length} columns</p>
            </div>
            {mappedRequiredMissing.length > 0 && (
              <div className="inline-flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Missing: {mappedRequiredMissing.join(", ")}
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4 font-medium">Your column</th>
                  <th className="py-2 pr-4 font-medium">Sample values</th>
                  <th className="py-2 font-medium">Maps to</th>
                </tr>
              </thead>
              <tbody>
                {headers.map((h) => (
                  <tr key={h} className="border-b border-slate-100" data-testid={`map-row-${h}`}>
                    <td className="py-3 pr-4 font-mono text-xs text-slate-800">{h}</td>
                    <td className="py-3 pr-4 text-xs text-slate-500 max-w-xs truncate">{rows.slice(0, 2).map(r => r[h]).filter(Boolean).join(" · ") || "—"}</td>
                    <td className="py-3">
                      <select value={mapping[h] || ""} onChange={e => setMapping({ ...mapping, [h]: e.target.value })} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm bg-white" data-testid={`map-select-${h}`}>
                        <option value="">— Skip this column —</option>
                        {TARGET_FIELDS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex items-center justify-between">
            <button onClick={() => { setStep(1); setFile(null); }} className="text-sm text-slate-600 hover:text-slate-900" data-testid="import-back-to-upload">Choose a different file</button>
            <button onClick={() => setStep(3)} disabled={mappedRequiredMissing.length > 0} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" style={{ background: "var(--primary-hex)" }} data-testid="import-continue-to-confirm">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Preview · first 5 rows</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    {activeFields.map(t => (
                      <th key={t.key} className="py-2 pr-4 font-medium">{t.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => (
                    // Preview rows have no natural id — the array is immutable
                    // for the lifetime of this component, so index-as-key is stable.
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={`preview-${i}`} className="border-b border-slate-100">
                      {activeFields.map(t => (
                        <td key={t.key} className="py-2 pr-4 text-slate-700 max-w-xs truncate">{String(row[t.key] ?? "")}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">If a matching contact already exists</h2>
            <p className="text-xs text-slate-500 mt-1">Match on email or last 10 digits of phone across BOTH your Leads and Clients — scoped to your workspace.</p>
            <div className="mt-4 space-y-2">
              {[
                { v: "skip", label: "Skip duplicates", desc: "Keep the existing lead as-is. Safest default." },
                { v: "update", label: "Update duplicates", desc: "Overwrite existing fields with non-empty values from the CSV." },
                { v: "create_new", label: "Create as new", desc: "Ignore the match — add a second record. Rare; use only if you know they're different people." },
              ].map(opt => (
                <label key={opt.v} className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${onDuplicate === opt.v ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="radio" name="dup" checked={onDuplicate === opt.v} onChange={() => setOnDuplicate(opt.v)} className="mt-0.5" data-testid={`import-dup-${opt.v}`} />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{opt.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="consent-callout rounded-md p-5">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 mt-0.5" style={{ color: "var(--primary-hex)" }} />
              <div>
                <div className="font-heading font-bold text-sm tracking-tight text-slate-900">Consent flags are legally consequential</div>
                <p className="text-xs text-slate-600 mt-1">If your CSV includes sms_consent / email_consent columns, they'll import as-is. Blank means no consent — automated outreach stays blocked. Make sure the CSV reflects reality; you're the one who signs the record.</p>
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={consentAcknowledge} onChange={e => setConsentAcknowledge(e.target.checked)} className="mt-0.5" data-testid="import-consent-ack" />
                  <span className="text-sm text-slate-800">I confirm the consent values in this file reflect what leads actually agreed to.</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button onClick={() => setStep(2)} className="text-sm text-slate-600 hover:text-slate-900" data-testid="import-back-to-map">Back to mapping</button>
            <button onClick={doImport} disabled={importing || !consentAcknowledge} className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40" style={{ background: "var(--primary-hex)" }} data-testid="import-run-btn">
              {importing ? "Importing…" : `Import ${rows.length} rows`}
            </button>
          </div>
        </div>
      )}

      {step === 4 && result && (
        <div className="space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
            <CheckCircle2 className="h-10 w-10 mx-auto" style={{ color: "var(--primary-hex)" }} />
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tighter text-slate-900">Import complete</h2>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-6 max-w-3xl mx-auto">
              {[
                { label: "Leads created",   value: result.created_leads,   tone: "text-emerald-700" },
                { label: "Clients created", value: result.created_clients, tone: "text-emerald-700" },
                { label: "Updated",         value: result.updated,         tone: "text-slate-900" },
                { label: "Skipped",         value: result.skipped,         tone: "text-slate-500" },
                { label: "Errors",          value: result.errored,         tone: result.errored ? "text-red-700" : "text-slate-500" },
              ].map(m => (
                <div key={m.label} data-testid={`import-metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className={`font-heading text-3xl sm:text-4xl font-bold tabular-nums ${m.tone}`}>{m.value}</div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{m.label}</div>
                </div>
              ))}
            </div>
          </div>

          {result.errored > 0 && (
            <div className="rounded-lg border border-red-200 bg-white p-6">
              <h3 className="font-heading font-bold text-slate-900">Rows that couldn't import</h3>
              <ul className="mt-3 space-y-1 text-xs font-mono">
                {erroredRows.map(r => (
                  <li key={r.row_index} className="text-red-700"><span className="text-slate-500">row {r.row_index + 2}:</span> {r.message}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Bulk-invite panel — only surfaces when the import actually
              created new Clients. Runs the exact same broker-disclosure
              guard the single-client endpoint does; the toast deep-links
              back to Settings if the agent hasn't finished the compliance
              form. */}
          {newlyCreatedClientIds.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="bulk-invite-panel">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900">
                    Send portal invites to {newlyCreatedClientIds.length} new client{newlyCreatedClientIds.length === 1 ? "" : "s"}?
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Each invite includes your broker disclosure and a magic-link that's valid for 7 days. Skips clients without an email on file.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={bulkInvite}
                  disabled={bulkInviting || !!bulkInviteResult}
                  className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 shrink-0"
                  style={{ background: "var(--primary-hex)" }}
                  data-testid="bulk-invite-btn"
                >
                  {bulkInviting ? "Sending…" : bulkInviteResult ? "Invites sent" : `Send ${newlyCreatedClientIds.length} portal invite${newlyCreatedClientIds.length === 1 ? "" : "s"}`}
                </button>
              </div>

              {bulkInviteResult && (
                <div className="mt-4 grid grid-cols-3 gap-4 text-center border-t border-slate-200 pt-4" data-testid="bulk-invite-summary">
                  <div>
                    <div className="font-heading text-2xl font-bold text-emerald-700 tabular-nums">{bulkInviteResult.sent}</div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mt-0.5">Sent</div>
                  </div>
                  <div>
                    <div className="font-heading text-2xl font-bold text-slate-500 tabular-nums">{bulkInviteResult.skipped}</div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mt-0.5">Skipped</div>
                  </div>
                  <div>
                    <div className={`font-heading text-2xl font-bold tabular-nums ${bulkInviteResult.errored ? "text-red-700" : "text-slate-500"}`}>{bulkInviteResult.errored}</div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mt-0.5">Errors</div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <button onClick={() => { setStep(1); setFile(null); setRows([]); setResult(null); setMapping({}); setConsentAcknowledge(false); setBulkInviteResult(null); }} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="import-another">Import another file</button>
            <button onClick={() => nav(backHref)} className="rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid={`import-view-${mode}`}>
              {mode === "clients" ? "View clients" : "View leads"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
